import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, ChartDataLabels);

const MINIMUM_LABEL_WIDTH = 34;
const MAXIMUM_LABEL_CATEGORIES = 10;

const getBarWidth = (context) => {
  const meta = context.chart.getDatasetMeta(context.datasetIndex);
  const element = meta?.data?.[context.dataIndex];
  const elementProperties = element?.getProps?.(['width'], true);

  return elementProperties?.width || element?.width || 0;
};

const shouldDisplayLabel = (context) => {
  const value = Number(context.dataset.data[context.dataIndex]);
  const categoryCount = context.chart.data.labels?.length || 0;

  return categoryCount <= MAXIMUM_LABEL_CATEGORIES
    && Number.isFinite(value)
    && value > 0
    && getBarWidth(context) >= MINIMUM_LABEL_WIDTH;
};

const formatDataLabel = (value, asPercentages) => {
  const numericValue = Number(value) || 0;

  return asPercentages ? `${numericValue.toFixed(2)}%` : `${numericValue}`;
};

const createDataLabelsOptions = (asPercentages, showDataLabels) => ({
  display: showDataLabels ? shouldDisplayLabel : false,
  formatter: (value) => formatDataLabel(value, asPercentages),
  anchor: 'end',
  align: 'left',
  color: '#ffffff',
  // textStrokeColor: 'rgba(0, 0, 0, 0.65)',
  font: {
		size: 11,
	},
  // textStrokeWidth: 2,
  clamp: true,
});

const ChartRenderer = ({ labelType, chartData, sampleSizes, labelTypes, asMixedSentiment, asPercentages, showDataLabels = true, onBarClick }) => {
  const labelCounts = chartData[labelType];
  const isSentimentEnabled = labelTypes[labelType]?.sentimentEnabled;

  let data = {};
  let options = {};

  if (labelType === 'Sentiment') {
    const keys = ['positive', 'negative', 'mixed', 'neutral', 'negative/mixed'];
    const baseColors = { positive: '#2B1056', negative: '#A9393A', mixed: '#CB6C75', neutral: '#A7A4B0', "negative/mixed": '#B94B66'};  
    const sentimentCounts = {};
    const sentimentColors = {};

    keys.forEach((key) => {
      if (Array.isArray(labelCounts[key])) {
        sentimentCounts[key] = labelCounts[key][0] || 0;
      }
    });
    
    for (const key in baseColors) {
      if (labelCounts[key]) {
        sentimentColors[key] = baseColors[key];
      }
    }

    const sortedSentimentsData = Object.keys(sentimentCounts)
      .map((sentiment) => ({ sentiment, count: sentimentCounts[sentiment] }))
      .sort((a, b) => b.count - a.count); // Sort in descending order based on counts

    const sortedLabels = sortedSentimentsData.map((item) => item.sentiment);
    const sortedCounts = sortedSentimentsData.map((item) => item.count);
    const sortedColors = sortedLabels.map((label) => sentimentColors[label]);

    data = {
      labels: sortedLabels.map((label) => label.charAt(0).toUpperCase() + label.slice(1)),
      datasets: [
        {
          label: labelType, // Use dynamic labelType
          data: sortedCounts,
          backgroundColor: sortedColors,
        },
      ],
    };

    options = {
      indexAxis: 'y',
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const clickedElement = elements[0];
          const sentimentLabel = sortedLabels[clickedElement.index];
          onBarClick('Sentiment', sentimentLabel, sentimentLabel.toLowerCase());
        }
      },
      scales: {
        x: { beginAtZero: true },
      },
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: false, // Disable legend
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const value = context.raw;

              if (asPercentages) 
                return `${context.dataset.label}: ${Array.isArray(value) ? `${(value[0] || 0).toFixed(2)}%` : `${(value || 0).toFixed(2)}%`}`;
              else 
                return `${context.dataset.label}: ${(value || 0)}`;
            },
          },
        },
        datalabels: createDataLabelsOptions(asPercentages, showDataLabels),
      },
    };
  } else {
    const labels = Object.keys(labelCounts);

    const sortedLabels = labels.sort((a, b) => {
      const totalA = isSentimentEnabled ? labelCounts[a]?.reduce((acc, val) => acc + val, 0) : labelCounts[a];
      const totalB = isSentimentEnabled ? labelCounts[b]?.reduce((acc, val) => acc + val, 0) : labelCounts[b];
      return totalB - totalA;
    });

    const baseConfig = asMixedSentiment
      ? [
          { label: 'Positive', index: 0, color: '#2B1056' },
          { label: 'Negative/Mixed', index: 1, color: '#B94B66' },
          { label: 'Neutral', index: 2, color: '#A7A4B0' },
        ]
      : [
          { label: 'Positive', index: 0, color: '#2B1056' },
          { label: 'Negative', index: 1, color: '#A9393A' },
          { label: 'Mixed', index: 2, color: '#CB6C75' },
          { label: 'Neutral', index: 3, color: '#A7A4B0' },
        ];

    data = isSentimentEnabled
      ? {
        labels: sortedLabels.length > 0 ? sortedLabels : ['No Data'],
        datasets: baseConfig.map(({ label, index, color }) => ({
          label,
          data: sortedLabels.map((labelKey) => labelCounts[labelKey]?.[index] || 0),
          backgroundColor: color,
        })),
      }
      : {
        labels: sortedLabels.length > 0 ? sortedLabels : ['No Data'],
        datasets: [
          {
            label: labelType, // Use dynamic labelType
            data: sortedLabels.map((label) => labelCounts[label] || 0),
            backgroundColor: ['#3BC1BE'],
          },
        ],
      };

    options = {
      indexAxis: 'y',
      scales: {
        x: { beginAtZero: true, stacked: isSentimentEnabled },
        y: { stacked: isSentimentEnabled },
      },
      responsive: true,
      maintainAspectRatio: true,
      onClick: (evt, elements) => {
        if (elements.length > 0) {
          const datasetIndex = elements[0].datasetIndex;
          const labelIndex = elements[0].index;
          const clickedLabel = data.labels[labelIndex];
          const sentiment = isSentimentEnabled ? data.datasets[datasetIndex].label : null;
          onBarClick(labelType, clickedLabel, sentiment);
        }
      },
      plugins: {
        legend: {
          display: false, // Disable legend
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const value = context.raw;

              if (asPercentages) 
                return `${context.dataset.label}: ${Array.isArray(value) ? `${(value[0] || 0).toFixed(2)}%` : `${(value || 0).toFixed(2)}%`}`;
              else 
                return `${context.dataset.label}: ${(value || 0)}`;
            },
          },
        },
        datalabels: createDataLabelsOptions(asPercentages, showDataLabels),
      },
    };
  }

  return <Bar data={data} options={options} width={500} height={250} />;
};

export default ChartRenderer;
